import React, { memo, useState } from 'react'
import { Handle, Position } from 'reactflow'
import { Box, Typography, useTheme } from '@mui/material'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import FactoryIcon from '@mui/icons-material/Factory'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import StorefrontIcon from '@mui/icons-material/Storefront'
import { useWorkspaceVisibility } from '../contexts/WorkspaceVisibilityContext'

const nodeConfig = {
  supplier:    { Icon: WarehouseIcon,     label: 'Supplier'     },
  factory:     { Icon: FactoryIcon,       label: 'Factory'      },
  distributor: { Icon: LocalShippingIcon, label: 'Distributor'  },
  retailer:    { Icon: StorefrontIcon,    label: 'Retailer'     },
}

const SupplyChainNode = ({ data, selected }) => {
  const [hovered, setHovered] = useState(false)
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { labels: showLabel } = useWorkspaceVisibility()

  const nodeType = data.nodeType || 'supplier'
  const config = nodeConfig[nodeType] || nodeConfig.supplier
  const { Icon } = config

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      {/* Hover name tooltip — only when labels are OFF */}
      {!showLabel && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            mb: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: '6px',
            background: isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.1)',
            opacity: hovered ? 1 : 0,
            pointerEvents: 'none',
            transition: 'opacity 0.15s ease',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: isDark ? '#e2e8f0' : '#1e293b',
              lineHeight: 1.2,
              letterSpacing: '0.01em',
            }}
          >
            {data.label}
          </Typography>
        </Box>
      )}

      {/* Node body */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 52,
          height: 52,
          borderRadius: '14px',
          cursor: 'pointer',
          position: 'relative',
          transition: 'box-shadow 0.2s ease, filter 0.2s ease',
          filter: hovered ? 'brightness(1.1)' : 'brightness(1)',
          outline: selected
            ? `2.5px solid ${isDark ? '#fff' : '#1e293b'}`
            : '2.5px solid transparent',
          outlineOffset: 3,
        }}
      >
        <Handle
          type="target"
          position={Position.Left}
          style={{
            width: 7, height: 7,
            background: isDark ? '#1e293b' : '#fff',
            border: `2px solid ${isDark ? '#64748b' : '#94a3b8'}`,
            borderRadius: '50%',
          }}
        />
        <Icon sx={{ fontSize: 28, color: '#fff', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' }} />
        <Handle
          type="source"
          position={Position.Right}
          style={{
            width: 7, height: 7,
            background: isDark ? '#1e293b' : '#fff',
            border: `2px solid ${isDark ? '#64748b' : '#94a3b8'}`,
            borderRadius: '50%',
          }}
        />
      </Box>

      {/* Persistent label — shown when labels are ON */}
      {showLabel && (
        <Typography
          sx={{
            mt: 0.5,
            fontSize: '11px',
            fontWeight: 600,
            color: isDark ? '#e2e8f0' : '#1e293b',
            whiteSpace: 'nowrap',
            textShadow: isDark
              ? '0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.5)'
              : '0 1px 3px rgba(255,255,255,0.9), 0 0 6px rgba(255,255,255,0.5)',
            letterSpacing: '0.01em',
            lineHeight: 1.2,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {data.label}
        </Typography>
      )}
    </Box>
  )
}

export default memo(SupplyChainNode)
